import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Alert, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import { getUserRolesList } from '../../modules/config';
import { useUserProjects } from '../../query/apiQueries';
import CustomInputField from '../../ui/primitives/CustomInputField';
import CustomSelect from '../../ui/primitives/CustomSelect';
import MultiSelectDropdown from '../../ui/primitives/MultiSelectDropdown';

const AddUserForm = forwardRef(
  ({ handleSubmit, isEditFlow = false, initialData = null }, ref) => {
    const formikRef = useRef(null);

    useImperativeHandle(ref, () => ({
      submit: () => formikRef.current?.submitForm(),
    }));

    const { data: rolesData = [] } = useQuery({
      queryKey: ['roles', 'list', 'org'],
      queryFn: () => getUserRolesList({ scope: 'org' }),
    });

    const roleOptions = rolesData
      .filter((r) => r.key !== 'org-admin')
      .map((r) => ({ id: r.id, name: r.name }));

    const getRoleKey = (roleId) =>
      rolesData.find((r) => r.id === roleId)?.key || '';

    const { data: projectData } = useUserProjects({
      perPage: 'all',
      page: 1,
      search: null,
      includeTestCases: false,
      includeLinkedProjects: true,
    });

    const projectOptions =
      projectData?.projects?.map((p) => ({
        id: p.id,
        name: p.name,
        jiraCloudId: p.jira_cloud_id,
      })) || [];

    const getInitialValues = () => {
      if (isEditFlow && initialData) {
        const resolvedRoleId =
          initialData.roleId ||
          rolesData.find((r) => r.key === initialData.role)?.id ||
          '';

        const mappedProjects = (initialData.assignment_details || []).map((detail) => {
          const globalProjectInfo = projectOptions.find((p) => p.id === detail.id);
          return {
            id: detail.id,
            name: detail.name,
            isAdmin: detail.role === 'project-admin' || detail.role === 'admin',
            jiraCloudId: globalProjectInfo?.jiraCloudId || null,
          };
        });

        return {
          first_name: initialData.first_name || '',
          last_name: initialData.last_name || '',
          email: initialData.email || '',
          role: resolvedRoleId,
          projects: mappedProjects,
        };
      }
      return {
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        projects: [],
      };
    };

    return (
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={Yup.object({
          first_name: Yup.string().max(255).required('First Name is required'),
          last_name: Yup.string().max(255),
          email: Yup.string()
            .email('Must be a valid email')
            .max(255)
            .required('Email is required'),
          role: Yup.string().required('Role is required'),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            const formattedValues = {
              ...values,
              first_name: values.first_name.trim(),
              last_name: values.last_name.trim(),
              email: values.email.trim(),
            };
            await handleSubmit(formattedValues);
            setStatus({ success: true });
          } catch (err) {
            setStatus({ success: false });
            setErrors({ submit: err.message });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleBlur, handleSubmit, setFieldValue }) => {
          const selectedRoleKey = getRoleKey(values.role);
          const isOrgMember = selectedRoleKey === 'org-member';

          return (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <CustomInputField
                    id="first_name"
                    label="First Name"
                    required
                    value={values.first_name}
                    placeholder="Enter first name"
                    onBlur={handleBlur}
                    onChange={(id, value) => setFieldValue(id, value)}
                    error={touched.first_name && errors.first_name}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomInputField
                    id="last_name"
                    label="Last Name"
                    value={values.last_name}
                    placeholder="Enter last name"
                    onBlur={handleBlur}
                    onChange={(id, value) => setFieldValue(id, value)}
                    error={touched.last_name && errors.last_name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <CustomInputField
                    id="email"
                    label="Email Address"
                    required
                    disabled={isEditFlow}
                    value={values.email}
                    placeholder="Enter email address"
                    onBlur={handleBlur}
                    onChange={(id, value) => setFieldValue(id, value)}
                    error={touched.email && errors.email}
                  />
                </Grid>

                <Grid item xs={12}>
                  <CustomSelect
                    id="role"
                    label="Role"
                    required
                    value={values.role}
                    options={roleOptions}
                    onChange={(value) => {
                      const newRoleKey = getRoleKey(value);
                      setFieldValue('role', value);
                      if (newRoleKey !== 'org-member') {
                        setFieldValue('projects', []);
                      } else {
                        const clearedProjects = values.projects.map((p) => ({
                          ...p,
                          isAdmin: false,
                        }));
                        setFieldValue('projects', clearedProjects);
                      }
                    }}
                    error={touched.role && errors.role}
                  />
                </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Users invited as Org Members are assigned the <strong>Team Member</strong> role in selected projects by default. 
                      To assign a different project role, visit the <strong>Project Team</strong> page after adding the user.
                    </Alert>
                  </Grid>
                {isOrgMember && (
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>
                      Projects
                    </Typography>
                    <MultiSelectDropdown
                      name="projects"
                      options={projectOptions}
                      selectedValues={values.projects.map((p) => p.id)}
                      searchable
                      showSelectAll
                      placeholder="Select Projects"
                      onChange={(selectedIds) => {
                        const updatedProjects = selectedIds.map((id) => {
                          const existing = values.projects.find((p) => p.id === id);
                          const projectInfo = projectOptions.find((p) => p.id === id);
                          return {
                            id,
                            name: projectInfo?.name || 'Unknown',
                            isAdmin: existing?.isAdmin || false,
                            jiraCloudId: projectInfo?.jiraCloudId || null,
                          };
                        });
                        setFieldValue('projects', updatedProjects);
                      }}
                    />
                  </Grid>
                )}

                {isOrgMember && values.projects.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>
                      Assign as Project Admin
                    </Typography>
                    <MultiSelectDropdown
                      name="projectAdmins"
                      options={values.projects.map((p) => ({ id: p.id, name: p.name }))}
                      selectedValues={values.projects.filter((p) => p.isAdmin).map((p) => p.id)}
                      searchable
                      showSelectAll
                      placeholder="Select Project Admins"
                      onChange={(selectedAdminIds) => {
                        const updatedProjects = values.projects.map((project) => ({
                          ...project,
                          isAdmin: selectedAdminIds.includes(project.id),
                        }));
                        setFieldValue('projects', updatedProjects);
                      }}
                    />
                  </Grid>
                )}

                {errors.submit && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="error">
                      {errors.submit}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </form>
          );
        }}
      </Formik>
    );
  },
);

export default AddUserForm;